// -----------------------------------------------------------------------------
// Reviewer feature — API surface.
//
// Wave 3 only owns Document Reviewer concerns; the canonical API DTOs live in
// `src/api/types.ts`. To avoid coupling other waves to reviewer-only shapes we
// keep those types co-located here and consume the shared `apiFetch` + `Endpoints`.
//
// Backend gaps (Phase 6 status):
//   * `PATCH  /api/v1/documents/{id}/fields/{name}`     — implemented (Wave 6.B)
//   * `POST   /api/v1/documents/{id}/revalidate`        — implemented (Wave 6.C)
//   * `GET    /api/v1/documents/{id}/audit-trail`       — implemented (Wave 6.A)
//   * `GET    /api/v1/documents/{id}/blob`              — implemented
// The remaining stubs resolve locally so the UI can be wired end-to-end. Each
// stub must be replaced with a real `apiFetch` call when its wave lands.
// -----------------------------------------------------------------------------

import { ApiError, apiFetch } from '../../api/client';
import { TokenStore } from '../../auth/TokenStore';
import { Endpoints } from '../../api/types';

// ---------- Status response ----------

/** Snake-case lifecycle status emitted by the backend. */
export type DocumentJobStatus =
  | 'queued'
  | 'processing'
  | 'approved'
  | 'review_required'
  | 'rejected'
  | 'failed'
  /** Terminal container: the upload was split into N independent child documents. */
  | 'split';

/**
 * Coarse pipeline stage the job is currently in, derived by the backend from the audit trail so the
 * polling UI can show live progress. Null once the job reaches a terminal status. `classifying` only
 * occurs for auto-classified jobs, between `queued` and `preparing`.
 */
export type DocumentProcessingStage =
  | 'queued'
  | 'classifying'
  | 'preparing'
  | 'ocr'
  | 'extracting'
  | 'validating'
  | 'finalizing';

/** Verbalised classification confidence band. */
export type ClassificationConfidenceBand = 'high' | 'medium' | 'low';

/**
 * Auto-classification provenance, present only when the job's document type was resolved by the
 * classifier rather than supplied at upload. Mirrors the backend `ClassificationResponse`. Carries no
 * document field values — evidence fragments are verified against the sanitised OCR excerpt.
 */
export interface ClassificationResponse {
  /** Always true when the block is present (the type came from auto-classification). */
  wasAutoClassified: boolean;
  /** Verbalised confidence band; null on legacy rows. */
  confidenceBand: ClassificationConfidenceBand | string | null;
  /** Who classified the job (e.g. `llm:claude-haiku-4-5`). */
  classifiedBy: string | null;
  /** Runner-up candidate slug the classifier considered, when any. */
  runnerUpSlug: string | null;
  /** Sanitised evidence fragments the classifier cited for its choice. */
  evidence: string[];
  /** True once a reviewer confirmed the classification (releasing the medium/low approval cap). */
  confirmed?: boolean;
  /** UTC instant of the reviewer's confirmation; null until confirmed. */
  confirmedAt?: string | null;
}

/** Mirrors `DocumentJobStatusResponse` (PascalCase JSON → camelCase here). */
export interface DocumentJobStatusResponse {
  jobId: string;
  status: DocumentJobStatus;
  submittedAt: string;
  completedAt: string | null;
  /**
   * True once the retention window elapsed and the document expired: the stored file was deleted and the
   * extracted values scrubbed. `status` keeps the original outcome, so treat this as an extra fact — the
   * blob endpoint will 404 from this point on.
   */
  expired?: boolean;
  currentStage?: DocumentProcessingStage | string | null;
  /** Auto-classification provenance; null for an explicitly-typed job (and until the type is resolved). */
  classification?: ClassificationResponse | null;
}

// ---------- Result response (canonical shape per PHASE_4 §DocumentResultResponse) ----------

/** Snake-case per-field status (matches `FieldConfidenceResponse.Status`). */
export type FieldStatus = 'ok' | 'warning' | 'critical';

/** Per-field entry of `DocumentConfidenceResponse.Fields`. */
export interface FieldConfidenceResponse {
  name: string;
  score: number;
  status: FieldStatus;
  value: unknown;
  issue?: string;
  /** Reviewer-only hint shown alongside `issue`; not always sent by the backend. */
  expectedHint?: string;
  /**
   * Present when the entry corresponds to a Group field and the response shape is `nested`
   * (the default for grouped schemas). Holds leaf FieldConfidences whose `name` retains the
   * qualified form (e.g. `cliente.razon_social`). Null on flat-shape responses or non-group fields.
   */
  children?: FieldConfidenceResponse[] | null;
}

/** Aggregate confidence block. */
export interface DocumentConfidenceResponse {
  globalScore: number;
  status: string;
  triggerReason: string;
  fields: ReadonlyArray<FieldConfidenceResponse>;
}

/**
 * OCR + LLM model identifiers surfaced on a processed document. Both fields are nullable:
 * `ocrModel` is null for legacy documents (forward-only capture); `llmModel` is null for
 * CU-routed jobs or pre-feature documents with no AiUsageEntry. Present when at least one
 * model id is known.
 */
export interface ProcessingModelsResponse {
  /** Azure Document Intelligence model id (e.g. `prebuilt-read`, `prebuilt-layout`). */
  ocrModel: string | null;
  /** Concrete LLM model identifier (e.g. `gpt-4.1-nano`, `claude-sonnet-4-6`). */
  llmModel: string | null;
}

/**
 * Non-sensitive processing metrics for the reviewer's metadata chips. Every field is nullable:
 * `pageCount`/`fileSizeBytes` are null when no OCR result is attached; the token/latency fields are
 * null when no successful LLM usage entry exists for the job. No cost figure is exposed (COGS is
 * admin-only). Mirrors `ProcessingMetricsResponse`.
 */
export interface ProcessingMetricsResponse {
  /** Pages detected by the OCR engine; null when no OCR result is attached. */
  pageCount: number | null;
  /** Original file size in bytes; null when no OCR result is attached. */
  fileSizeBytes: number | null;
  /** End-to-end latency of the LLM extraction call in milliseconds; null when no usage entry exists. */
  latencyMs: number | null;
  /** Prompt tokens consumed by the extraction call; null when no usage entry exists. */
  inputTokens: number | null;
  /** Completion tokens produced by the extraction call; null when no usage entry exists. */
  outputTokens: number | null;
  /** Prompt tokens served from the provider cache; null when unknown or not reported. */
  cachedInputTokens: number | null;
}

/**
 * A typed structural block (Mistral OCR 4) with a page-normalised bounding box ([0,1], top-left
 * origin), for the reviewer's structure overlay. Mirrors `BlockResponse`.
 */
export interface BlockResponse {
  /** 1-based page number. */
  pageNumber: number;
  /** Block type label (e.g. `title`, `table`, `signature`, `header`). */
  blockType: string;
  /** Left edge, page-normalised [0,1]. */
  x: number;
  /** Top edge, page-normalised [0,1]. */
  y: number;
  /** Width, page-normalised [0,1]. */
  width: number;
  /** Height, page-normalised [0,1]. */
  height: number;
  /** Short text snippet for a tooltip; null when empty. */
  content?: string | null;
  /** Engine confidence [0,1] when reported; otherwise null. */
  confidence?: number | null;
}

/** Mirrors `DocumentResultResponse`. */
export interface DocumentResultResponse {
  documentId: string;
  documentType: string;
  documentTypeVersion: string;
  status: DocumentJobStatus;
  submittedAt: string;
  completedAt: string | null;
  /**
   * True once the retention window elapsed and the document expired: the stored file was deleted and every
   * extracted value scrubbed. `data` and the per-field values inside `confidence` are therefore empty by
   * policy, not because extraction failed — show "caducado", never an empty-result error.
   */
  expired?: boolean;
  confidence: DocumentConfidenceResponse | null;
  data: Record<string, unknown> | null;
  /** OCR + LLM model identifiers. Null when neither is known. */
  processingModels?: ProcessingModelsResponse | null;
  /** Parent job id when this document is a sub-document of a split upload; null otherwise. */
  parentJobId?: string | null;
  /** 0-based position among its siblings; null for non-split jobs. */
  segmentIndex?: number | null;
  /** Label the segmenter assigned (e.g. the recurring identifier value); null if none. */
  segmentLabel?: string | null;
  /** First page (1-based, inclusive) of the original file this sub-document occupies. */
  startPage?: number | null;
  /** Last page (1-based, inclusive) of the original file this sub-document occupies. */
  endPage?: number | null;
  /** Reviewer user id this document is assigned to (J1); null when unassigned. */
  assignedTo?: string | null;
  /** Typed structural blocks (Mistral OCR 4) for the structure overlay; null when the OCR engine returns no layout. */
  structuralBlocks?: ReadonlyArray<BlockResponse> | null;
  /** Non-sensitive processing metrics (pages, size, latency, tokens) for the header chips; null for legacy documents. */
  metrics?: ProcessingMetricsResponse | null;
  /** Auto-classification provenance; null for an explicitly-typed job. Drives the reviewer's badge + correction affordance. */
  classification?: ClassificationResponse | null;
}

// ---------- Children (split parent — GET /documents/{id}/children) ----------

/**
 * One sub-document of a split parent. Mirrors the backend `ChildDocumentSummary`. Each child is a
 * normal job retrievable via `/documents/{jobId}/result`; `startPage`/`endPage` are the 1-based,
 * inclusive page range it occupied in the original file.
 */
export interface ChildDocumentSummary {
  jobId: string;
  segmentIndex: number | null;
  label: string | null;
  startPage: number | null;
  endPage: number | null;
  /** Snake-case lifecycle status of the child job. */
  status: DocumentJobStatus | string;
}

/** Mirrors the backend `DocumentChildrenResponse`. Empty `children` for an ordinary job. */
export interface DocumentChildrenResponse {
  parentJobId: string;
  children: ChildDocumentSummary[];
}

/**
 * Lists the independent sub-documents a split (multi-document) upload was divided into, from
 * `GET /api/v1/documents/{id}/children`. Returns an empty list for an ordinary, non-split job.
 */
export function listDocumentChildren(
  jobId: string,
  signal?: AbortSignal,
): Promise<DocumentChildrenResponse> {
  return apiFetch<DocumentChildrenResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/children`,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Audit trail (Wave 6.A — GET /documents/{id}/audit-trail) ----------

/**
 * Single entry in the chronological audit trail. Mirrors the backend
 * `AuditTrailEntryResponse` DTO (PascalCase JSON → camelCase here).
 *
 * The backend records orchestrator + activity events (e.g.
 * `preprocessing_started`, `ai_extraction_completed`, `validation_completed`,
 * `manual_correction`). Field values are never included on this surface; only
 * lifecycle metadata.
 */
export interface AuditTrailEntryResponse {
  eventType: string;
  /** ISO instant the event was recorded. */
  at: string;
  actorId: string;
  durationMs?: number | null;
  /** Engine/provider/route metadata. Null for legacy entries. Keys: engineLabel, providerType,
   *  modelName, providerId, analyzerId, finalRoute, attemptCount, inputTokens, outputTokens,
   *  pageCount, textChars, markdownChars, candidateFieldCount, fieldCount, violationsCount,
   *  warningsCount, validationStatus, globalScore, triggerReason, failureReason, fieldName,
   *  webhookUrlHost, httpStatusCode, deliveryFailureReason, deliveryAttempt, diResourceId,
   *  licenseType, chosenRoute,
   *  segmentationStrategy, segmentCount, segmentationReason (document_segmented event). */
  details?: Record<string, string> | null;
}

/**
 * One row in the Fase 8 preparation cascade. Mirrors the backend `RouteAttemptResponse`.
 * The reviewer panel renders these as ordered badges (R1 → R2 → R3 …) so the operator can see
 * which route the pipeline tried first, which fell back, and why a route was dropped.
 */
export interface RouteAttemptResponse {
  /** R1 … R6. */
  route: string;
  /** `success`, `fallback`, `timeout`, `circuit_open`, `unsupported`, `error`. */
  outcome: string;
  /** Wall-clock latency of this single attempt, in milliseconds. */
  latencyMs: number;
  /** Populated only for `error` / `timeout` outcomes. */
  errorCode?: string | null;
  /** Populated when the route was dropped pre-attempt (e.g. `cu_budget_hard`). */
  dropReason?: string | null;
}

/** Preparation-cascade summary surfaced alongside the audit trail. Null for legacy jobs. */
export interface RoutingTraceResponse {
  /** Route that produced the final NormalizedDocument. */
  finalRoute: string;
  /** Sum of all attempt latencies. */
  totalLatencyMs: number;
  /** Ordered attempt history. */
  attempts: RouteAttemptResponse[];
}

/** Mirrors the backend `AuditTrailResponse` DTO. */
export interface AuditTrailResponse {
  entries: AuditTrailEntryResponse[];
  /** Present when the job ran through the Document Preparation Layer (Fase 8). */
  routingTrace?: RoutingTraceResponse | null;
}

// ---------- Endpoints ----------

function statusUrl(jobId: string): string {
  return `${Endpoints.documents}/${encodeURIComponent(jobId)}/status`;
}

function resultUrl(jobId: string): string {
  return `${Endpoints.documents}/${encodeURIComponent(jobId)}/result`;
}

/**
 * Fetches the lifecycle status of a document job.
 * Used by the reviewer page to poll while the job is in flight.
 */
export function getDocumentStatus(jobId: string): Promise<DocumentJobStatusResponse> {
  return apiFetch<DocumentJobStatusResponse>(statusUrl(jobId));
}

/**
 * Fetches the terminal result of a document job (extracted data + confidence).
 * The backend rejects calls for non-terminal jobs with 409; callers should
 * gate on `getDocumentStatus` first.
 */
export function getDocumentResult(jobId: string): Promise<DocumentResultResponse> {
  return apiFetch<DocumentResultResponse>(resultUrl(jobId));
}

// ---------- Blob source ----------

function blobUrl(jobId: string): string {
  return `${Endpoints.documents}/${encodeURIComponent(jobId)}/blob`;
}

function resolveBaseUrl(): string {
  const raw = import.meta.env.VITE_API_BASE_URL?.trim() ?? '';
  if (raw === '') {
    return '';
  }
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

/**
 * Downloads the original document blob from the API.
 *
 * apiFetch only knows how to decode JSON/text, so we bypass it and issue the
 * fetch directly — we still need the Bearer header that apiFetch normally
 * injects, so we pull it from TokenStore the same way. The result is an
 * object URL the caller passes straight to PDF.js (`getDocument({ url })`);
 * the caller is responsible for `URL.revokeObjectURL` on cleanup.
 */
export async function fetchDocumentBlobObjectUrl(jobId: string): Promise<string> {
  const token = TokenStore.get();
  const path = blobUrl(jobId);
  const url = `${resolveBaseUrl()}${path}`;

  const headers = new Headers();
  if (token !== null) {
    headers.set('Authorization', `Bearer ${token.accessToken}`);
  }

  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch (cause) {
    throw new ApiError(0, 'Network error', { kind: 'network', raw: cause });
  }

  if (!response.ok) {
    throw new ApiError(response.status, `Failed to load document blob (HTTP ${response.status})`, {
      kind: response.status === 404 ? 'not_found' : 'unknown',
    });
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

// ---------- Stubs (disabled in UI; resolve locally for type-safety) ----------

/**
 * Body of `PATCH /api/v1/documents/{id}/fields/{name}` (Wave 6.B).
 * Mirrors the backend `PatchFieldRequest` DTO except that `FieldName` is the
 * URL segment, not part of the body. `rowIndex` + `column` are reserved for
 * Table-cell patches (the reviewer table view doesn't surface them yet).
 */
export interface PatchFieldBody {
  value: unknown;
  rowIndex?: number;
  column?: string;
}

/**
 * Records a manual correction to a field of a completed job. Returns the
 * refreshed `DocumentResultResponse` so the caller can replace its cache entry
 * without an additional GET.
 */
export function patchField(
  jobId: string,
  fieldName: string,
  body: PatchFieldBody,
): Promise<DocumentResultResponse> {
  return apiFetch<DocumentResultResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/fields/${encodeURIComponent(fieldName)}`,
    { method: 'PATCH', json: body },
  );
}

/**
 * Assigns the document to a reviewer (J1), or unassigns it when `assigneeUserId` is null. Returns the
 * refreshed `DocumentResultResponse` so the caller can replace its cache entry. "Assign to me" is just
 * passing the current user's id.
 */
export function setDocumentAssignee(
  jobId: string,
  assigneeUserId: string | null,
): Promise<DocumentResultResponse> {
  return apiFetch<DocumentResultResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/assignee`,
    { method: 'PATCH', json: { assigneeUserId } },
  );
}

/**
 * Re-runs the deterministic validation + scoring layers against the job's current ExtractedData
 * snapshot (no IA call) and returns the refreshed `DocumentResultResponse`. The backend recomputes
 * the document-level status: a banner sitting in `review_required` flips to `approved` once the
 * accumulated manual corrections (via PATCH) satisfy every schema rule. See Wave 6.C.
 */
export function revalidateDocument(jobId: string): Promise<DocumentResultResponse> {
  return apiFetch<DocumentResultResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/revalidate`,
    { method: 'POST' },
  );
}

/**
 * Records the reviewer's confirmation that the auto-classified type is correct: the backend
 * releases the medium/low classification-confidence cap and re-validates in the same request, so a
 * document whose only blocker was `classification_confidence:{band}` comes back `approved`.
 */
export function confirmClassification(jobId: string): Promise<DocumentResultResponse> {
  return apiFetch<DocumentResultResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/confirm-classification`,
    { method: 'POST' },
  );
}

/**
 * Fetches the chronological audit trail of a document job from
 * `GET /api/v1/documents/{jobId}/audit-trail` (Wave 6.A).
 */
export function getAuditTrail(jobId: string, signal?: AbortSignal): Promise<AuditTrailResponse> {
  return apiFetch<AuditTrailResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/audit-trail`,
    signal !== undefined ? { signal } : {},
  );
}

/** Mirrors the backend `DocumentRawAiResponse` DTO. */
export interface DocumentRawAiResponse {
  jobId: string;
  rawAiJson?: string | null;
}

/**
 * Fetches the verbatim AI extraction output stored on the job. Gated by the
 * `platform:debug` scope on the backend — non-superuser principals get 403, which
 * callers translate into "hide the tab" rather than show an error.
 */
export function getDocumentRawAiResponse(
  jobId: string,
  signal?: AbortSignal,
): Promise<DocumentRawAiResponse> {
  return apiFetch<DocumentRawAiResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/raw-ai-json`,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Comments (J2 — GET/POST /documents/{id}/comments) ----------

/** Mirrors the backend `DocumentCommentResponse`. */
export interface DocumentCommentResponse {
  id: string;
  jobId: string;
  authorUserId: string;
  body: string;
  createdAt: string; // ISO 8601
}

/** Lists the reviewer comment thread for a job, oldest first. */
export function getDocumentComments(jobId: string, signal?: AbortSignal): Promise<DocumentCommentResponse[]> {
  return apiFetch<DocumentCommentResponse[]>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/comments`,
    signal !== undefined ? { signal } : {},
  );
}

/** Adds a comment authored by the current user (the backend stamps the author from the JWT). */
export function addDocumentComment(jobId: string, body: string): Promise<DocumentCommentResponse> {
  return apiFetch<DocumentCommentResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/comments`,
    { method: 'POST', json: { body } },
  );
}

/**
 * Marker used by hooks/components to surface "disabled" tooltips for reviewer features that have
 * not been wired yet. Empty as of Wave 6.C — all reviewer endpoints are implemented.
 */
export const REVIEWER_FEATURES_PENDING = {} as const;
